import React from 'react'
import ReactDOM from 'react-dom'
import { format } from 'date-fns'
import Markdown from 'marked-react'
import { getExportStyles, convertSectionsToMarkdown } from './helpers'

/**
 * Exports the Automation Insights report to PDF via browser's print dialog.
 *
 * Why this approach:
 * - Avoids adding a new dependency & the feature is org-1 only
 * - No server-side rendering needed
 * - Styles must be embedded inline since iframe can't access external stylesheets
 *
 * ALTERNATIVE:
 * - html2pdf.js: Adds needs new dependency
 * - Server-side PDF: Loss of client-side styles and adds infrastructure complexity
 */
export const exportToPDF = ({ report, summaryCards }) => {
  const reportDate = format(new Date(), 'yyyy-MM-dd')
  const reportDateLabel = format(new Date(), 'MMM d, yyyy')
  const logoUrl = `${window.location.origin}/balto-email-logo.png`

  const escape = (str) =>
    str
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;')

  const summaryHTML = summaryCards
    .map(
      (card) => `
      <div class="summary-card">
        <div class="label">${escape(card.label)}</div>
        <div class="value">${escape(card.value)}</div>
      </div>`
    )
    .join('')

  const markdown = convertSectionsToMarkdown(report.sections || [])
  let contentHTML = ''
  try {
    const container = document.createElement('div')
    ReactDOM.render(
      <Markdown gfm breaks>
        {markdown}
      </Markdown>,
      container
    )
    contentHTML = container.innerHTML
    ReactDOM.unmountComponentAtNode(container)
  } catch {
    contentHTML = `<pre>${escape(markdown)}</pre>`
  }

  // Create hidden iframe to isolate print styles from main app
  const iframe = document.createElement('iframe')
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'
  iframe.setAttribute('aria-hidden', 'true')
  iframe.srcdoc = `<!doctype html>
    <html>
    <head>
      <meta charset="utf-8"/>
      <title>Automation Insights - ${reportDate}</title>
      <link rel="stylesheet" href="data:text/css;base64,${btoa(getExportStyles())}"/>
    </head>
    <body>
      <div class="ai-export">
        <div class="header">
          <div>
            <div class="title">Automation Insights Report</div>
            <div class="date">${escape(reportDateLabel)}</div>
          </div>
          <img src="${logoUrl}" alt="Balto" class="logo"/>
        </div>
        <div class="summary">${summaryHTML}</div>
        <div class="content">${contentHTML}</div>
      </div>
    </body>
    </html>
  `

  const removeIframe = () => {
    if (document.body.contains(iframe)) {
      iframe.remove()
    }
  }

  iframe.onload = () => {
    const win = iframe.contentWindow
    if (win) {
      win.focus()
      win.print()
      setTimeout(removeIframe, 1000)
    } else {
      removeIframe()
    }
  }

  iframe.onerror = removeIframe

  document.body.appendChild(iframe)
  setTimeout(removeIframe, 30000)
}
